import React, { useState, useEffect, useRef } from 'react';
import AppLayout from './components/AppLayout';
import Dashboard from './pages/Dashboard';
import StoryEditor from './pages/StoryEditor';
import Archive from './pages/Archive';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import Planning from './pages/Planning';
import MyAssignments from './pages/MyAssignments';
import WritingTool from './pages/WritingTool';
import AllStories from './pages/AllStories';
import EditorDesk from './pages/EditorDesk';
import Help from './pages/Help';
import Login from './pages/Login';
import { supabase } from './services/supabaseClient';
import { logAction } from './services/store';
import { Session } from '@supabase/supabase-js';
import { Loader2, ShieldAlert } from 'lucide-react';

// --- CONFIGURATION ---
// Set this to your organization's domain to auto-block others.
// Set to null to allow any domain (and rely on manual role assignment).
const RESTRICT_TO_DOMAIN = null; // e.g., 'tolonews.com'

const App: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [currentView, setCurrentView] = useState('writing_tool');
  const [activeStoryId, setActiveStoryId] = useState<string | undefined>(undefined);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Security & Loading States
  const [isAccessChecking, setIsAccessChecking] = useState(false);
  const [accessDenied, setAccessDenied] = useState<string | null>(null);

  // Ref to prevent double-logging in React 18 StrictMode
  const hasLoggedLogin = useRef(false);

  useEffect(() => {
    // 1. Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) checkAccessAndLog(session);
    });

    // 2. Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) checkAccessAndLog(session);
      else {
        // Reset state on logout
        hasLoggedLogin.current = false;
        setAccessDenied(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // --- SECURITY & LOGGING LOGIC ---
  const checkAccessAndLog = async (currentSession: Session) => {
    if (hasLoggedLogin.current) return; // Prevent double execution
    setIsAccessChecking(true);
    hasLoggedLogin.current = true;

    try {
      const user = currentSession.user;

      // A. DOMAIN CHECK (Optional)
      if (RESTRICT_TO_DOMAIN) {
        const emailDomain = user.email?.split('@')[1];
        if (emailDomain !== RESTRICT_TO_DOMAIN) {
          setAccessDenied(`Access restricted to @${RESTRICT_TO_DOMAIN} emails only.`);
          await supabase.auth.signOut();
          setIsAccessChecking(false);
          return;
        }
      }

      // B. PROFILE/ROLE CHECK
      // Fetch user profile to check if suspended
      const { data: profile } = await supabase
        .from('profiles')
        .select('role, full_name')
        .eq('id', user.id)
        .single();

      if (profile?.role === 'suspended') {
        setAccessDenied("Your account has been suspended by an administrator.");
        await supabase.auth.signOut();
        setIsAccessChecking(false);
        return;
      }

      // C. LOGIN AUDIT LOGGING
      // Note: IP geolocation via third-party (ipapi.co) was removed — it shared
      // journalist IPs with an external service without consent.
      await logAction(
        "user_login",
        "user",
        user.id,
        {}
      );

      // Update last_login on the user's profile
      await supabase
        .from('users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', user.id);

    } catch (error) {
      console.error("Access check failed:", error);
    } finally {
      setIsAccessChecking(false);
    }
  };

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  const handleNavigate = (view: string) => {
    setCurrentView(view);
    if (view !== 'story_editor') {
      setActiveStoryId(undefined);
    }
  };

  const handleEditStory = (id: string) => {
    setActiveStoryId(id);
    setCurrentView('story_editor');
  };

  const handleNewStory = () => {
    setActiveStoryId(undefined);
    setCurrentView('story_editor');
  };

  // --- RENDER STATES ---

  // 1. Loading Access Check
  if (session && isAccessChecking) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
        <p className="text-zinc-500">Verifying credentials & location...</p>
      </div>
    );
  }

  // 2. Access Denied Screen
  if (accessDenied) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-red-50 dark:bg-zinc-950 p-4">
        <div className="bg-white dark:bg-zinc-900 p-8 rounded-xl shadow-sm max-w-md text-center border border-red-100 dark:border-red-900">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="h-8 w-8 text-red-600 dark:text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-zinc-600 dark:text-zinc-400 mb-6">{accessDenied}</p>
          <button
            onClick={() => setAccessDenied(null)}
            className="px-4 py-2 bg-zinc-900 dark:bg-zinc-800 text-white rounded-lg hover:opacity-90 transition-all duration-200 active:scale-[0.98]"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // 3. Not Logged In
  if (!session) {
    return <Login />;
  }

  // 4. Main App (Render content based on current view)
  const renderContent = () => {
    switch (currentView) {
      case 'writing_tool':
        return <WritingTool />;
      case 'my_assignments':
        return <MyAssignments onEditStory={handleEditStory} />;
      case 'dashboard':
        return <Dashboard onEditStory={handleEditStory} onNewStory={handleNewStory} />;
      case 'story_editor':
        return (
          <StoryEditor
            storyId={activeStoryId}
            onExit={() => handleNavigate('my_assignments')}
          />
        );
      case 'archive':
        return <Archive />;
      case 'planning':
        return <Planning onEditStory={handleEditStory} />;
      case 'admin':
        return <Admin />;
      case 'settings':
        return <Settings />;
      case 'all_stories':
        return <AllStories onEditStory={(storyId) => {
          // Store storyId and navigate to WritingTool
          // WritingTool will check for this on mount
          sessionStorage.setItem('openStoryId', storyId);
          setCurrentView('writing_tool');
        }} />;
      case 'editor_desk':
        return <EditorDesk onEditStory={(storyId) => {
          sessionStorage.setItem('openStoryId', storyId);
          setCurrentView('writing_tool');
        }} />;
      case 'help':
        return <Help />;
      default:
        return <WritingTool />;
    }
  };

  const isFocusMode = currentView === 'story_editor';

  return (
    <AppLayout
      currentView={currentView}
      onChangeView={handleNavigate}
      isDarkMode={isDarkMode}
      toggleTheme={toggleTheme}
      showSidebar={!isFocusMode}
    >
      {renderContent()}
    </AppLayout>
  );
};

export default App;
